// SMART goal scoring and tracking helpers.
// The scorer is deliberately deterministic and local: it gives instant feedback
// as the user types, while the AI coach (backend) gives the deep critique.

export const SMART_CRITERIA = [
  { key: 'specific',   letter: 'S', labelKey: 'smartGoals.criteria.specific.name',   hintKey: 'smartGoals.criteria.specific.hint' },
  { key: 'measurable', letter: 'M', labelKey: 'smartGoals.criteria.measurable.name', hintKey: 'smartGoals.criteria.measurable.hint' },
  { key: 'achievable', letter: 'A', labelKey: 'smartGoals.criteria.achievable.name', hintKey: 'smartGoals.criteria.achievable.hint' },
  { key: 'relevant',   letter: 'R', labelKey: 'smartGoals.criteria.relevant.name',   hintKey: 'smartGoals.criteria.relevant.hint' },
  { key: 'timeBound',  letter: 'T', labelKey: 'smartGoals.criteria.timeBound.name',  hintKey: 'smartGoals.criteria.timeBound.hint' }
];

export const GOAL_CATEGORIES = ['business', 'personal', 'career', 'health', 'finance', 'learning', 'team'];
export const REVIEW_FREQUENCIES = ['daily', 'weekly', 'biweekly', 'monthly', 'quarterly'];
export const GOAL_STATUSES = ['not-started', 'on-track', 'at-risk', 'completed'];

const clamp = (n) => Math.max(0, Math.min(100, Math.round(n)));

// Score a free-text field by how much substance it carries.
const textScore = (value, { good = 80, ok = 35 } = {}) => {
  const text = (value || '').trim();
  if (!text) return 0;
  if (text.length >= good) return 100;
  if (text.length >= ok) return 65;
  return 30;
};

// Pull the first number out of a string like "$12,500/month" → 12500
export const parseNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (!value) return null;
  const match = String(value).replace(/[\s,]/g, '').match(/-?\d+(\.\d+)?/);
  return match ? parseFloat(match[0]) : null;
};

const hasNumber = (value) => parseNumber(value) !== null;

export const daysUntil = (dateString) => {
  if (!dateString) return null;
  const due = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due - today) / 86400000);
};

// ── Per-criterion scores ─────────────────────────────────────────────────────

const scoreSpecific = (goal) => {
  let score = textScore(goal.specific, { good: 90, ok: 40 }) * 0.7;
  if ((goal.title || '').trim().length >= 6) score += 20;
  if ((goal.owner || '').trim()) score += 10;
  return clamp(score);
};

const scoreMeasurable = (goal) => {
  const m = goal.measurable || {};
  let score = 0;
  if ((m.metric || '').trim()) score += 30;
  if ((m.baseline || '').toString().trim()) score += 20;
  if ((m.target || '').toString().trim()) score += 30;
  if ((m.unit || '').trim()) score += 10;
  // A target that is an actual number is worth more than an adjective.
  if (hasNumber(m.target) && hasNumber(m.baseline)) score += 10;
  else if (hasNumber(m.target)) score += 5;
  return clamp(score);
};

const scoreAchievable = (goal) => {
  let score = textScore(goal.achievable, { good: 90, ok: 40 }) * 0.6;
  if ((goal.milestones || []).length >= 2) score += 20;
  else if ((goal.milestones || []).length === 1) score += 10;
  const plannedObstacles = (goal.obstacles || []).filter(o => (o.plan || '').trim());
  if (plannedObstacles.length >= 2) score += 20;
  else if (plannedObstacles.length === 1) score += 10;
  return clamp(score);
};

const scoreRelevant = (goal) => {
  // Relevance can be stated briefly and still be strong, so the bar for a
  // "full" answer is lower here than for the Specific and Achievable fields.
  let score = textScore(goal.relevant, { good: 55, ok: 25 }) * 0.8;
  if (goal.linked_objective !== null && goal.linked_objective !== undefined && goal.linked_objective !== '') score += 20;
  return clamp(score);
};

const scoreTimeBound = (goal) => {
  const t = goal.time_bound || {};
  let score = 0;
  if (t.due_date) {
    score += 50;
    const remaining = daysUntil(t.due_date);
    if (remaining !== null && remaining > 0) score += 20;
  }
  if (t.start_date) score += 15;
  if (t.review_frequency) score += 15;
  return clamp(score);
};

/**
 * Full SMART score for a goal.
 * Returns { total, breakdown: { specific, measurable, achievable, relevant, timeBound } }
 */
export const scoreSmartGoal = (goal = {}) => {
  const breakdown = {
    specific: scoreSpecific(goal),
    measurable: scoreMeasurable(goal),
    achievable: scoreAchievable(goal),
    relevant: scoreRelevant(goal),
    timeBound: scoreTimeBound(goal)
  };
  const values = Object.values(breakdown);
  const total = clamp(values.reduce((sum, v) => sum + v, 0) / values.length);
  return { total, breakdown };
};

export const scoreBand = (score) => {
  if (score >= 85) return 'excellent';
  if (score >= 70) return 'solid';
  if (score >= 45) return 'draft';
  return 'vague';
};

/** Bright band colour — for bars, rings and other fills. */
export const scoreColor = (score) => {
  if (score >= 85) return '#22c55e';
  if (score >= 70) return '#38bdf8';
  if (score >= 45) return '#fbbf24';
  return '#ef4444';
};

/**
 * The same bands as theme tokens, for text and icons: the bright fills are
 * legible on a dark surface but not on a white one, so the light theme swaps
 * in darker shades.
 */
export const scoreTextColor = (score) => {
  if (score >= 85) return 'var(--status-green)';
  if (score >= 70) return 'var(--status-sky)';
  if (score >= 45) return 'var(--status-amber)';
  return 'var(--status-red)';
};

/**
 * Progress from the goal's own metric when baseline/current/target are numeric,
 * otherwise the manually set progress value.
 */
export const computeProgress = (goal = {}) => {
  const m = goal.measurable || {};
  const baseline = parseNumber(m.baseline);
  const target = parseNumber(m.target);
  const current = parseNumber(m.current);

  if (baseline !== null && target !== null && current !== null && target !== baseline) {
    const pct = ((current - baseline) / (target - baseline)) * 100;
    return clamp(pct);
  }
  return clamp(goal.progress || 0);
};

/**
 * Health of a goal: compares how far along the metric is against how much of
 * the calendar has been consumed.
 */
export const goalHealth = (goal = {}) => {
  const progress = computeProgress(goal);
  if (progress >= 100 || goal.status === 'completed') return 'completed';

  const t = goal.time_bound || {};
  const start = t.start_date ? new Date(`${t.start_date}T00:00:00`) : null;
  const due = t.due_date ? new Date(`${t.due_date}T00:00:00`) : null;
  if (!start || !due || Number.isNaN(start.getTime()) || Number.isNaN(due.getTime()) || due <= start) {
    return progress > 0 ? 'on-track' : 'not-started';
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (today < start) return 'not-started';

  const elapsed = ((today - start) / (due - start)) * 100;
  if (today > due) return 'at-risk';
  // More than 15 points behind the calendar means the goal is slipping.
  return progress + 15 < elapsed ? 'at-risk' : 'on-track';
};

export const nextMilestone = (goal = {}) =>
  (goal.milestones || [])
    .filter(m => !m.completed && m.date)
    .sort((a, b) => a.date.localeCompare(b.date))[0] || null;

// Portfolio-level roll-up used by the summary strip.
export const summarizeGoals = (goals = []) => {
  if (goals.length === 0) {
    return { count: 0, avgScore: 0, avgProgress: 0, onTrack: 0, atRisk: 0, completed: 0, dueSoon: 0 };
  }
  let scoreSum = 0;
  let progressSum = 0;
  const tally = { 'on-track': 0, 'at-risk': 0, completed: 0, 'not-started': 0 };
  let dueSoon = 0;

  goals.forEach(goal => {
    scoreSum += scoreSmartGoal(goal).total;
    progressSum += computeProgress(goal);
    tally[goalHealth(goal)] += 1;
    const remaining = daysUntil(goal.time_bound?.due_date);
    if (remaining !== null && remaining >= 0 && remaining <= 14) dueSoon += 1;
  });

  return {
    count: goals.length,
    avgScore: Math.round(scoreSum / goals.length),
    avgProgress: Math.round(progressSum / goals.length),
    onTrack: tally['on-track'],
    atRisk: tally['at-risk'],
    completed: tally.completed,
    dueSoon
  };
};
